import React from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import { CssBaseline } from "@mui/material";
import { SnackbarProvider } from "notistack";
import { QueryClient, QueryClientProvider } from "react-query";

// Context Providers
import { AuthProvider } from "./contexts/AuthContext";
import { TenantProvider } from "./contexts/TenantContext";

// Components
import ProtectedRoute from "./components/ProtectedRoute";
import Layout from "./components/Layout";

// Pages
import LoginPage from "./pages/auth/LoginPage";
import RegisterPage from "./pages/auth/RegisterPage";
import ForgotPasswordPage from "./pages/auth/ForgotPasswordPage";
import Dashboard from "./pages/Dashboard";
import Workers from "./pages/Workers";
import Contractors from "./pages/Contractors";
import Attendance from "./pages/Attendance";
import Compliance from "./pages/Compliance";
import Reports from "./pages/Reports";
import Settings from "./pages/Settings";
import Devices from "./pages/Devices";
import Notifications from "./pages/Notifications";
import SuperAdminDashboard from "./pages/superadmin/Dashboard";
import TenantManagement from "./pages/superadmin/TenantManagement";
import Profile from "./pages/Profile";
import NotFound from "./pages/NotFound";

// New Components
import ShiftManagement from "./components/Shifts/ShiftManagement";
import AttendancePolicies from "./components/Attendance/AttendancePolicies";
import StatutoryReportsManagement from "./components/StatutoryReports/StatutoryReportsManagement";

// Additional Components
import RosterManagement from "./pages/RosterManagement";
import WorkOrders from "./pages/WorkOrders";

// Create theme
const theme = createTheme({
  palette: {
    primary: {
      main: "#1976d2",
      light: "#42a5f5",
      dark: "#1565c0",
    },
    secondary: {
      main: "#dc004e",
      light: "#ff5983",
      dark: "#9a0036",
    },
    background: {
      default: "#f5f5f5",
      paper: "#ffffff",
    },
  },
  typography: {
    fontFamily:
      '-apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif',
    h4: {
      fontWeight: 600,
    },
    h6: {
      fontWeight: 500,
    },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: "none",
          borderRadius: 8,
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          boxShadow: "0 2px 12px rgba(0,0,0,0.1)",
        },
      },
    },
  },
});

// Create React Query client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <SnackbarProvider
          maxSnack={3}
          anchorOrigin={{
            vertical: "top",
            horizontal: "right",
          }}
        >
          <AuthProvider>
            <TenantProvider>
              <Router>
                <Routes>
                  {/* Public Routes */}
                  <Route path="/login" element={<LoginPage />} />
                  <Route path="/register" element={<RegisterPage />} />
                  <Route
                    path="/forgot-password"
                    element={<ForgotPasswordPage />}
                  />

                  {/* Protected Routes */}
                  <Route
                    path="/"
                    element={
                      <ProtectedRoute>
                        <Layout />
                      </ProtectedRoute>
                    }
                  >
                    <Route
                      index
                      element={<Navigate to="/dashboard" replace />}
                    />
                    <Route path="dashboard" element={<Dashboard />} />
                    <Route path="workers" element={<Workers />} />
                    <Route path="contractors" element={<Contractors />} />
                    <Route path="attendance" element={<Attendance />} />
                    <Route path="shifts" element={<ShiftManagement />} />
                    <Route
                      path="attendance-policies"
                      element={<AttendancePolicies />}
                    />
                    <Route path="compliance" element={<Compliance />} />
                    <Route
                      path="statutory-reports"
                      element={<StatutoryReportsManagement />}
                    />
                    <Route path="clra-reports" element={<Reports />} />
                    <Route path="analytics-reports" element={<Reports />} />
                    <Route
                      path="roster-management"
                      element={<RosterManagement />}
                    />
                    <Route path="work-orders" element={<WorkOrders />} />
                    <Route path="reports" element={<Reports />} />
                    <Route path="devices" element={<Devices />} />
                    <Route path="notifications" element={<Notifications />} />
                    <Route path="settings" element={<Settings />} />
                    <Route path="profile" element={<Profile />} />

                    {/* Super Admin Routes */}
                    <Route
                      path="superadmin/dashboard"
                      element={<SuperAdminDashboard />}
                    />
                    <Route
                      path="superadmin/tenants"
                      element={<TenantManagement />}
                    />
                  </Route>

                  {/* 404 Route */}
                  <Route path="*" element={<NotFound />} />
                </Routes>
              </Router>
            </TenantProvider>
          </AuthProvider>
        </SnackbarProvider>
      </ThemeProvider>
    </QueryClientProvider>
  );
}

export default App;
